import React from 'react';
import { Row, Col, Card, Typography, Button } from 'antd';
import { RiseOutlined, FallOutlined, LockOutlined, CrownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { BenchmarkMetric } from './types';
import { getPerformanceLevel, calculateAbsoluteDifference } from './benchmarkThresholds';

const { Text } = Typography;

interface MetricsCardsProps {
  metrics: BenchmarkMetric[];
  maxItems?: number;
  isElementLocked?: (elementName: string) => boolean;
}

export const MetricsCards: React.FC<MetricsCardsProps> = ({ metrics, maxItems, isElementLocked }) => {
  const navigate = useNavigate();

  const getPerformanceColors = (absoluteDiff: number) => {
    const perfLevel = getPerformanceLevel(absoluteDiff);

    const colorMaps = {
      'excellent': {
        bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
        border: '#16a34a',
        accent: '#15803d',
        text: '#14532d',
        badgeColor: '#15803d',
        badgeBg: '#dcfce7',
        performance: 'excellent' as const
      },
      'very-good': {
        bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        border: '#22c55e',
        accent: '#16a34a',
        text: '#15803d',
        badgeColor: '#16a34a',
        badgeBg: '#ecfdf5',
        performance: 'very-good' as const
      },
      'good': {
        bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        border: '#4ade80',
        accent: '#22c55e',
        text: '#16a34a',
        badgeColor: '#22c55e',
        badgeBg: '#f0fdf4',
        performance: 'good' as const
      },
      'fair': {
        bg: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
        border: '#f87171',
        accent: '#ef4444',
        text: '#dc2626',
        badgeColor: '#ef4444',
        badgeBg: '#fef2f2',
        performance: 'fair' as const
      },
      'poor': {
        bg: 'linear-gradient(135deg, #fef2f2 0%, #fca5a5 100%)',
        border: '#ef4444',
        accent: '#dc2626',
        text: '#b91c1c',
        badgeColor: '#dc2626',
        badgeBg: '#fef2f2',
        performance: 'poor' as const
      },
      'critical': {
        bg: 'linear-gradient(135deg, #fee2e2 0%, #f87171 100%)',
        border: '#dc2626',
        accent: '#b91c1c',
        text: '#991b1b',
        badgeColor: '#b91c1c',
        badgeBg: '#fee2e2',
        performance: 'very-poor' as const
      }
    };

    return colorMaps[perfLevel.performance];
  };

  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;

  return (
    <Row gutter={[24, 24]}>
      {displayMetrics.map((metric) => {
        // Check if this element is locked
        const locked = isElementLocked ? isElementLocked(metric.categoryName) : false;

        // Calculate absolute difference instead of percentage difference
        const absoluteDifference = calculateAbsoluteDifference(metric.value, metric.industryMean || 0);

        const colors = getPerformanceColors(absoluteDifference);

        // Locked card styling
        if (locked) {
          return (
            <Col
              xs={24}
              sm={12}
              md={12}
              lg={6}
              key={metric.categoryId}
            >
              <Card
                styles={{ body: { padding: '20px' } }}
                style={{
                  height: '220px',
                  background: 'linear-gradient(135deg, rgba(156, 163, 175, 0.15) 0%, rgba(107, 114, 128, 0.25) 100%)',
                  borderRadius: '16px',
                  border: '2px solid #9ca3af',
                  boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  cursor: 'not-allowed',
                  opacity: 0.85
                }}>
                  {/* Category Name - Fixed height for 2 lines */}
                  <div style={{
                    marginTop: '16px',
                    marginBottom: '0',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center'
                  }}>
                    <Text style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: '#374151',
                      lineHeight: '1.3',
                      display: 'block'
                    }}>
                      {metric.categoryName}
                    </Text>
                  </div>

                  {/* Locked Content */}
                  <div style={{
                    flex: '1 0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <LockOutlined style={{
                      fontSize: '28px',
                      color: '#9ca3af',
                      marginBottom: '12px'
                    }} />
                    <Text style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontWeight: 600,
                      marginBottom: '16px'
                    }}>
                      Premium Feature
                    </Text>
                    <Button
                      type="primary"
                      size="small"
                      icon={<CrownOutlined />}
                      onClick={() => navigate('/dashboard/organisation?tab=billing&showUpgrade=true')}
                      style={{
                        background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                        border: 'none',
                        borderRadius: '6px'
                      }}
                    >
                      Upgrade
                    </Button>
                  </div>
              </Card>
            </Col>
          );
        }

        return (
          <Col
            xs={24}
            sm={12}
            md={12}
            lg={6}
            key={metric.categoryId}
          >
            <Card
              styles={{ body: { padding: '20px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' } }}
              style={{
                height: '220px',
                background: colors.bg,
                borderRadius: '16px',
                border: `2px solid ${colors.border}`,
                boxShadow: '0 4px 20px -4px rgba(0, 0, 0, 0.15)',
                overflow: 'hidden',
                position: 'relative',
                transition: 'all 0.2s ease'
              }}>
              {/* Performance Badge - Most Prominent */}
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: colors.badgeBg,
                border: `2px solid ${colors.badgeColor}`,
                borderRadius: '16px',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                zIndex: 2
              }}>
                {absoluteDifference > 0 ? (
                  <RiseOutlined style={{ color: colors.badgeColor, fontSize: '14px' }} />
                ) : absoluteDifference < 0 ? (
                  <FallOutlined style={{ color: colors.badgeColor, fontSize: '14px' }} />
                ) : null}
                <Text style={{
                  color: colors.badgeColor,
                  fontSize: '13px',
                  fontWeight: 700,
                  margin: 0
                }}>
                  {metric.industryMean ?
                    `${absoluteDifference > 0 ? '+' : ''}${absoluteDifference.toFixed(1)}%` :
                    'No Data'
                  }
                </Text>
              </div>
                {/* Category Name - Fixed height for 2 lines */}
                <div style={{
                  marginTop: '16px',
                  marginBottom: '0',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>
                  <Text style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: colors.text,
                    lineHeight: '1.3',
                    display: 'block'
                  }}>
                    {metric.categoryName}
                  </Text>
                </div>

                {/* Score */}
                <div style={{
                  flex: '1 0 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center'
                }}>
                  <Text style={{
                    fontSize: '36px',
                    fontWeight: 800,
                    color: colors.text,
                    lineHeight: '1',
                    marginBottom: '12px'
                  }}>
                    {metric.value.toFixed(1)}%
                  </Text>

                  <span style={{
                    fontSize: '11px',
                    color: colors.badgeColor,
                    fontWeight: 600,
                    background: colors.badgeBg,
                    border: `1px solid ${colors.badgeColor}`,
                    borderRadius: '12px',
                    padding: '3px 12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {colors.performance === 'excellent' ? 'Outstanding' :
                     colors.performance === 'very-good' ? 'Strong' :
                     colors.performance === 'good' ? 'Above Average' :
                     colors.performance === 'fair' ? 'Below Average' :
                     colors.performance === 'poor' ? 'Needs Work' : 'Critical'}
                  </span>
                </div>

                {/* Industry Comparison */}
                <div style={{
                  flex: '0 0 auto',
                  textAlign: 'center',
                  paddingTop: '12px'
                }}>
                  <Text style={{
                    color: colors.text,
                    fontSize: '11px',
                    opacity: 0.6,
                    display: 'block'
                  }}>
                    Industry Avg
                  </Text>
                  <Text style={{
                    color: colors.text,
                    fontSize: '15px',
                    fontWeight: 700,
                    display: 'block',
                    marginTop: '2px'
                  }}>
                    {metric.industryMean ? `${metric.industryMean.toFixed(1)}%` : 'N/A'}
                  </Text>
                </div>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};